import { getFormErrorMessage } from "@core/utility/get-error-message";
import { LevelFormRule, LevelFormSchema } from "@factory/form/form.rule";
import { useFormDefaultLevel } from "@factory/form/form-default-level.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseLevelModel } from "@factory/model/query-model-level";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";
import {
  useQyDeleteLevel,
  useQyGetLevelById,
  useQyUpdateLevel,
} from "@factory/query/level.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditLevel() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultLevel();
  const navigate = useNavigate();
  const { mapUpdateLevel, mapDeleteLevel } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { levelId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API VIEW ASSET
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();

  // API GETBYID Level
  const handleGetApiSuccess = (data: GetResponseLevelModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    if (responseData?.avatar) {
      viewAsset(responseData?.avatar, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setValue("file", assetDisplay);
        },
      });
    }

    setValue("tenant", responseData?.tenant || "");
    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
    setValue("avatar", responseData?.avatar || "");
    setValue("points", responseData?.points);
    setValue("stamp", responseData?.stamp);
    setValue("constraint", responseData?.constraint || "");
    setValue("maxStamp", responseData?.max_stamp);
    setValue("maxPoints", responseData?.max_points);
    setValue("maxStampPerDay", responseData?.max_stamp_per_day);
    setValue("maxPointsPerDay", responseData?.max_points_per_day);
  };
  const {
    data: levelResponse,
    isLoading,
    isError: levelError,
  } = useQyGetLevelById(levelId || "", handleGetApiSuccess);
  const levelData = levelResponse?.data?.[0];

  // API UPDATE Level
  const handleUpdateApiSuccess = () => {
    showSuccess("Level updated successfully");
  };
  const { mutate: updateLevel, isLoading: isEditLoading } = useQyUpdateLevel(
    handleUpdateApiSuccess
  );

  // API DELETE Level
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Level deleted successfully");
  };
  const { mutate: deleteLevel } = useQyDeleteLevel(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<LevelFormSchema>({
    defaultValues: getDefault(levelData),
    resolver: zodResolver(LevelFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: LevelFormSchema) => {
    if (!levelData || !levelData.code) {
      throw new Error("No level data");
    }

    const formData = mapUpdateLevel(form, levelData.code);
    updateLevel(formData);
  };
  const handleValidateError = (err: FieldErrors<LevelFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!levelData || !levelData.code) {
      throw new Error("No level data found");
    }

    const form = mapDeleteLevel(levelData.code);
    deleteLevel(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    levelId,
    levelData,
    isLoading,
    levelError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
